"use client";
import { useState, useEffect, useMemo } from "react";
import { Plus, X, ChevronDown } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

// ── STATUS & PRIORITY CONFIG ──
const STATUS_CFG: Record<string, { label: string; color: string; dot: string }> = {
  todo:   { label: "Pendiente",   color: "var(--tx3)", dot: "var(--tx3)" },
  doing:  { label: "En progreso", color: "var(--run)", dot: "var(--run)" },
  review: { label: "En revisión", color: "var(--warn)", dot: "var(--warn)" },
  done:   { label: "Hecho",       color: "var(--ok)",  dot: "var(--ok)" },
};

const PRIORITY_CFG: Record<string, { label: string; bg: string; color: string }> = {
  low:    { label: "Baja",    bg: "rgba(148,166,189,.12)", color: "var(--tx3)" },
  med:    { label: "Media",   bg: "var(--acc-dim)", color: "var(--acc)" },
  high:   { label: "Alta",    bg: "rgba(246,178,74,.14)", color: "var(--warn)" },
  urgent: { label: "Urgente", bg: "rgba(255,107,107,.14)", color: "var(--bad)" },
};

type ViewMode = "grid" | "kanban" | "list";

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  due_date?: string;
  assignee_id?: string;
  assignee_type?: "agent" | "member";
  project_id?: string;
  parent_id?: string;
  position?: number;
  subtask_total?: number;
  subtask_done?: number;
  created_at: string;
}

interface Project {
  id: string;
  name: string;
  emoji?: string;
  color?: string;
  task_count?: number;
  done_pct?: number;
}

interface Agent { id: string; name: string; emoji: string; }
interface Member { id: number; name: string; emoji: string; color: string; }

// ── UTILS ──
const isOverdue = (due?: string, status?: string) => {
  if (!due || status === "done") return false;
  return new Date(due) < new Date();
};

const isSoonDue = (due?: string, status?: string) => {
  if (!due || status === "done") return false;
  const d = new Date(due);
  const now = new Date();
  const days = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return days >= 0 && days <= 7;
};

// ── MAIN ──
export default function TasksPage() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Filtros combinables
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<string | null>(null);
  const [filterDue, setFilterDue] = useState<string | null>(null);
  const [filterAssignee, setFilterAssignee] = useState<string | null>(null);

  const load = async () => {
    const [t, p, a, m] = await Promise.all([
      fetch(`${API}/tasks-v2?top_level=1`).then(r => r.json()),
      fetch(`${API}/projects-v2`).then(r => r.json()),
      fetch(`${API}/agents`).then(r => r.json()),
      fetch(`${API}/team-members`).then(r => r.json()).catch(() => []),
    ]);
    setTasks(t);
    setProjects(p);
    setAgents(a);
    setMembers(m);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let result = tasks;

    // Filtro Proyecto
    if (selectedProjectId) {
      if (selectedProjectId === "none") {
        result = result.filter(t => !t.project_id);
      } else {
        result = result.filter(t => t.project_id === selectedProjectId);
      }
    }

    // Filtro Estado
    if (filterStatus) {
      result = result.filter(t => t.status === filterStatus);
    }

    // Filtro Prioridad
    if (filterPriority) {
      result = result.filter(t => t.priority === filterPriority);
    }

    // Filtro Vence
    if (filterDue) {
      result = result.filter(t => {
        if (!t.due_date) return filterDue === "none";
        const d = new Date(t.due_date);
        const now = new Date();
        const days = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (filterDue === "overdue") return days < 0;
        if (filterDue === "thisweek") return days >= 0 && days <= 7;
        if (filterDue === "thismonth") return days >= 0 && days <= 30;
        return false;
      });
    }

    // Filtro Responsable
    if (filterAssignee) {
      if (filterAssignee === "none") {
        result = result.filter(t => !t.assignee_id);
      } else if (filterAssignee.startsWith("agent:")) {
        const agId = filterAssignee.replace("agent:", "");
        result = result.filter(t => t.assignee_type === "agent" && t.assignee_id === agId);
      } else if (filterAssignee.startsWith("member:")) {
        const mId = filterAssignee.replace("member:", "");
        result = result.filter(t => t.assignee_type === "member" && t.assignee_id === mId);
      }
    }

    return result;
  }, [tasks, selectedProjectId, filterStatus, filterPriority, filterDue, filterAssignee]);

  const updateTask = async (id: string, patch: Partial<Task>) => {
    await fetch(`${API}/tasks-v2/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    load();
  };

  const createTask = async (data: Partial<Task>) => {
    await fetch(`${API}/tasks-v2`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    load();
  };

  const deleteTask = async (id: string) => {
    await fetch(`${API}/tasks-v2/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div style={{ display: "flex", width: "100%", minHeight: "calc(100vh - 80px)", gap: "16px" }}>
      {/* SIDEBAR */}
      <Sidebar
        projects={projects}
        selected={selectedProjectId}
        onSelect={setSelectedProjectId}
      />

      {/* MAIN */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
        <Header
          viewMode={viewMode}
          onViewChange={setViewMode}
          taskCount={filtered.length}
          onNew={() => setShowCreate(true)}
        />

        <FilterBar
          projects={projects}
          agents={agents}
          members={members}
          filterProject={selectedProjectId}
          filterStatus={filterStatus}
          filterPriority={filterPriority}
          filterDue={filterDue}
          filterAssignee={filterAssignee}
          onProjectChange={setSelectedProjectId}
          onStatusChange={setFilterStatus}
          onPriorityChange={setFilterPriority}
          onDueChange={setFilterDue}
          onAssigneeChange={setFilterAssignee}
          onClear={() => {
            setSelectedProjectId(null);
            setFilterStatus(null);
            setFilterPriority(null);
            setFilterDue(null);
            setFilterAssignee(null);
          }}
        />

        {viewMode === "grid" && (
          <GridView tasks={filtered} projects={projects} agents={agents} members={members} onUpdate={updateTask} onSelect={setSelectedTask} />
        )}

        {viewMode === "kanban" && (
          <KanbanView tasks={filtered} projects={projects} agents={agents} members={members} onUpdate={updateTask} onSelect={setSelectedTask} />
        )}

        {viewMode === "list" && (
          <ListView tasks={filtered} projects={projects} agents={agents} members={members} onUpdate={updateTask} onSelect={setSelectedTask} />
        )}

        {selectedTask && (
          <TaskDetailModal
            task={selectedTask}
            projects={projects}
            agents={agents}
            members={members}
            onClose={() => setSelectedTask(null)}
            onUpdate={async (patch) => { await updateTask(selectedTask.id, patch); setSelectedTask(null); }}
            onDelete={async () => { await deleteTask(selectedTask.id); setSelectedTask(null); }}
          />
        )}

        {showCreate && (
          <TaskCreateModal
            projects={projects}
            agents={agents}
            members={members}
            defaultProjectId={selectedProjectId}
            onClose={() => setShowCreate(false)}
            onCreate={async (data) => { await createTask(data); setShowCreate(false); }}
          />
        )}
      </div>
    </div>
  );
}

// ── FILTER BAR ──
function FilterBar({ projects, agents, members, filterProject, filterStatus, filterPriority, filterDue, filterAssignee,
  onProjectChange, onStatusChange, onPriorityChange, onDueChange, onAssigneeChange, onClear }: any) {

  const activeCount = [filterProject, filterStatus, filterPriority, filterDue, filterAssignee].filter(Boolean).length;

  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: ".6px", color: "var(--tx3)", marginRight: "2px" }}>Filtrar:</span>

      {/* Proyecto */}
      <FilterChip
        label="Proyecto"
        active={!!filterProject}
        onClear={() => onProjectChange(null)}
        options={[
          { value: "none", label: "Sin proyecto" },
          ...projects.map((p: Project) => ({ value: p.id, label: `${p.emoji} ${p.name}` })),
        ]}
        value={filterProject}
        onChange={onProjectChange}
      />

      {/* Estado */}
      <FilterChip
        label="Estado"
        active={!!filterStatus}
        onClear={() => onStatusChange(null)}
        options={[
          { value: "todo", label: "Pendiente" },
          { value: "doing", label: "En progreso" },
          { value: "review", label: "En revisión" },
          { value: "done", label: "Hecho" },
        ]}
        value={filterStatus}
        onChange={onStatusChange}
      />

      {/* Prioridad */}
      <FilterChip
        label="Prioridad"
        active={!!filterPriority}
        onClear={() => onPriorityChange(null)}
        options={[
          { value: "low", label: "Baja" },
          { value: "med", label: "Media" },
          { value: "high", label: "Alta" },
          { value: "urgent", label: "Urgente" },
        ]}
        value={filterPriority}
        onChange={onPriorityChange}
      />

      {/* Vence */}
      <FilterChip
        label="Vence"
        active={!!filterDue}
        onClear={() => onDueChange(null)}
        options={[
          { value: "overdue", label: "Vencidas" },
          { value: "thisweek", label: "Esta semana" },
          { value: "thismonth", label: "Este mes" },
          { value: "none", label: "Sin fecha" },
        ]}
        value={filterDue}
        onChange={onDueChange}
      />

      {/* Responsable */}
      <FilterChip
        label="Responsable"
        active={!!filterAssignee}
        onClear={() => onAssigneeChange(null)}
        options={[
          { value: "none", label: "Sin asignar" },
          ...agents.map((a: Agent) => ({ value: `agent:${a.id}`, label: `${a.emoji} ${a.name}` })),
          ...members.map((m: Member) => ({ value: `member:${m.id}`, label: `👤 ${m.name}` })),
        ]}
        value={filterAssignee}
        onChange={onAssigneeChange}
      />

      {activeCount > 0 && (
        <span onClick={onClear} style={{ fontSize: "11px", color: "var(--tx3)", cursor: "pointer", textDecoration: "underline", marginLeft: "4px" }}>
          Limpiar filtros
        </span>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClear, options, value, onChange }: any) {
  const [open, setOpen] = useState(false);
  const selectedLabel = value ? options.find((o: any) => o.value === value)?.label : null;

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: active ? "var(--acc-dim)" : "var(--panel)",
          border: `1px solid ${active ? "var(--acc)" : "var(--line)"}`,
          borderRadius: "8px",
          padding: "6px 12px",
          fontSize: "11.5px",
          color: active ? "var(--acc)" : "var(--tx2)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontFamily: "inherit",
          transition: "border-color .12s",
        }}
        onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.borderColor = "var(--tx3)"; }}
        onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.borderColor = "var(--line)"; }}
      >
        {selectedLabel ? `${label}: ${selectedLabel}` : `${label} ▾`}
        {active && (
          <X
            size={13}
            style={{ color: "var(--tx3)" }}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              setOpen(false);
            }}
          />
        )}
      </button>

      {open && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9,
            }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            background: "var(--panel)",
            border: "1px solid var(--line)",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,.3)",
            minWidth: "180px",
            zIndex: 10,
            overflow: "hidden",
          }}>
            {options.map((opt: any) => (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 14px",
                  fontSize: "12px",
                  cursor: "pointer",
                  background: value === opt.value ? "var(--panel2)" : "transparent",
                  color: value === opt.value ? "var(--acc)" : "var(--tx2)",
                  transition: "background .12s",
                }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--panel2)")}
                onMouseLeave={e => { if (value !== opt.value) (e.currentTarget.style.background = "transparent"); }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── SIDEBAR ──
function Sidebar({ projects, selected, onSelect }: {
  projects: Project[];
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div style={{
      width: "240px",
      background: "var(--panel)",
      borderRadius: "12px",
      border: "1px solid var(--line)",
      padding: "16px 0",
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      alignSelf: "flex-start",
      position: "sticky",
      top: "20px",
    }}>
      <div style={{ padding: "0 14px 10px", fontSize: "10px", fontWeight: 700, color: "var(--tx3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Proyectos
      </div>

      <SidebarItem emoji="📋" label="Todas" active={selected === null} onClick={() => onSelect(null)} />
      <SidebarItem emoji="📦" label="Sin proyecto" active={selected === "none"} onClick={() => onSelect("none")} />

      <div style={{ height: "1px", background: "var(--line2)", margin: "8px 0" }} />

      {projects.map(p => (
        <SidebarItem
          key={p.id}
          emoji={p.emoji || "📁"}
          label={p.name}
          active={selected === p.id}
          onClick={() => onSelect(p.id)}
          meta={`${p.task_count || 0}`}
          color={p.color}
        />
      ))}
    </div>
  );
}

function SidebarItem({ emoji, label, active, onClick, meta, color }: any) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "7px 14px",
        cursor: "pointer",
        background: active ? "var(--panel2)" : "transparent",
        borderLeft: active ? `3px solid ${color || "var(--acc)"}` : "3px solid transparent",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "12px",
        transition: "all 120ms",
        color: active ? "var(--tx)" : "var(--tx2)",
      }}
    >
      <span style={{ fontSize: "13px" }}>{emoji}</span>
      <span style={{ flex: 1, fontWeight: active ? 600 : 400 }}>{label}</span>
      {meta && <span style={{ fontSize: "10px", color: "var(--tx3)" }}>{meta}</span>}
    </div>
  );
}

// ── HEADER ──
function Header({ viewMode, onViewChange, taskCount, onNew }: {
  viewMode: ViewMode;
  onViewChange: (v: ViewMode) => void;
  taskCount: number;
  onNew: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <h1 style={{ fontSize: "22px", fontWeight: 700, color: "var(--tx)", letterSpacing: "-.4px" }}>Tareas</h1>
        <p style={{ fontSize: "12px", color: "var(--tx3)", marginTop: "3px" }}>
          <b style={{ color: "var(--tx2)" }}>{taskCount} tareas</b>
        </p>
      </div>

      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        {/* Conmutador de vistas */}
        <div style={{ display: "flex", background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: "9px", padding: "3px", gap: "2px" }}>
          <ViewBtn label="▦ Tablero" active={viewMode === "grid"} onClick={() => onViewChange("grid")} />
          <ViewBtn label="▤ Kanban" active={viewMode === "kanban"} onClick={() => onViewChange("kanban")} />
          <ViewBtn label="☰ Lista" active={viewMode === "list"} onClick={() => onViewChange("list")} />
        </div>

        <button onClick={onNew} style={{
          padding: "8px 15px",
          background: "var(--acc)",
          color: "#06101f",
          border: "none",
          borderRadius: "9px",
          fontSize: "12.5px",
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 0 16px var(--acc-glow)",
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}>
          <Plus size={13} /> Nueva tarea
        </button>
      </div>
    </div>
  );
}

function ViewBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 13px",
        fontSize: "12px",
        color: active ? "var(--acc)" : "var(--tx3)",
        background: active ? "var(--acc-dim)" : "transparent",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontFamily: "inherit",
        fontWeight: active ? 600 : 400,
        transition: "all 120ms",
      }}
    >
      {label}
    </button>
  );
}

// ── GRID VIEW (Tablero) ──
function GridView({ tasks, projects, agents, members, onUpdate, onSelect }: {
  tasks: Task[];
  projects: Project[];
  agents: Agent[];
  members: Member[];
  onUpdate: (id: string, patch: Partial<Task>) => Promise<void>;
  onSelect: (t: Task) => void;
}) {
  if (tasks.length === 0) {
    return <EmptyState />;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }} className="task-grid">
      {tasks.map(t => {
        const proj = projects.find(p => p.id === t.project_id);
        const assignee = getAssignee(t, agents, members);
        const overdue = isOverdue(t.due_date, t.status);
        const soon = isSoonDue(t.due_date, t.status);

        return (
          <div
            key={t.id}
            style={{
              background: "var(--panel)",
              border: "1px solid var(--line)",
              borderRadius: "11px",
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              gap: "9px",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              transition: "border-color .12s, transform .12s",
              minHeight: "118px",
            }}
            onClick={() => onSelect(t)}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = "var(--tx3)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = "var(--line)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {/* Franja lateral */}
            <div style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: proj?.color || "var(--tx3)",
            }} />

            {/* Top */}
            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "flex-start" }}>
              <div style={{ fontSize: "13px", lineHeight: 1.35, fontWeight: 500, color: "var(--tx)", flex: 1 }}>
                {t.title}
              </div>
              {t.priority && <PriorityPill priority={t.priority} />}
            </div>

            {/* Proyecto */}
            <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10.5px", color: "var(--tx3)" }}>
              <span style={{
                width: "7px",
                height: "7px",
                borderRadius: "2px",
                background: proj?.color || "var(--tx3)",
                flexShrink: 0,
              }} />
              <span>{proj?.emoji} {proj?.name || "Sin proyecto"}</span>
            </div>

            {/* Foot */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
              {t.status === "doing" ? (
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: "var(--run)",
                }}>
                  <span style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "var(--run)",
                    boxShadow: "0 0 8px var(--run)",
                  }} />
                  En progreso
                </span>
              ) : assignee ? (
                <span style={{
                  fontSize: "11px",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  background: assignee.bg,
                  color: assignee.color,
                }}>
                  {assignee.label}
                </span>
              ) : (
                <span style={{ fontSize: "11px", color: "var(--tx3)" }}>Sin asignar</span>
              )}

              {t.due_date && (
                <span style={{
                  fontSize: "11px",
                  fontFamily: "'JetBrains Mono', monospace",
                  color: overdue ? "var(--bad)" : soon ? "var(--warn)" : "var(--tx2)",
                }}>
                  {new Date(t.due_date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── KANBAN VIEW ──
function KanbanView({ tasks, projects, agents, members, onUpdate, onSelect }: {
  tasks: Task[];
  projects: Project[];
  agents: Agent[];
  members: Member[];
  onUpdate: (id: string, patch: Partial<Task>) => Promise<void>;
  onSelect: (t: Task) => void;
}) {
  const columns = ["todo", "doing", "review", "done"] as const;

  const tasksByStatus = useMemo(() => {
    const map: Record<string, Task[]> = {};
    columns.forEach(s => { map[s] = []; });
    tasks.forEach(t => { map[t.status]?.push(t); });
    return map;
  }, [tasks]);

  if (tasks.length === 0) {
    return <EmptyState />;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", alignItems: "start" }}>
      {columns.map(status => {
        const cfg = STATUS_CFG[status];
        const statusTasks = tasksByStatus[status] || [];

        return (
          <div
            key={status}
            style={{
              background: "var(--panel2)",
              border: "1px solid var(--line2)",
              borderRadius: "12px",
              padding: "11px",
            }}
          >
            {/* Column header */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "11px", padding: "0 2px" }}>
              <span style={{
                fontSize: "11.5px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: ".4px",
                display: "flex",
                alignItems: "center",
                gap: "7px",
                color: cfg.color,
              }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: cfg.dot }} />
                {cfg.label}
              </span>
              <span style={{ fontSize: "11px", color: "var(--tx3)", fontFamily: "'JetBrains Mono', monospace" }}>
                {statusTasks.length}
              </span>
            </div>

            {/* Cards */}
            {statusTasks.map(t => {
              const proj = projects.find(p => p.id === t.project_id);
              const assignee = getAssignee(t, agents, members);
              const overdue = isOverdue(t.due_date, t.status);
              const soon = isSoonDue(t.due_date, t.status);

              return (
                <div
                  key={t.id}
                  style={{
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderRadius: "9px",
                    padding: "11px",
                    marginBottom: "8px",
                    cursor: "pointer",
                    transition: "border-color .12s",
                    opacity: status === "done" ? 0.6 : 1,
                  }}
                  onClick={() => onSelect(t)}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--tx3)")}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--line)")}
                >
                  <div style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "8px",
                    lineHeight: 1.3,
                    color: "var(--tx)",
                  }}>
                    {t.title}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "6px" }}>
                    {t.priority && <PriorityPill priority={t.priority} />}
                    {assignee && (
                      <span style={{
                        fontSize: "11px",
                        padding: "2px 7px",
                        borderRadius: "6px",
                        background: assignee.bg,
                        color: assignee.color,
                      }}>
                        {assignee.label}
                      </span>
                    )}
                    {t.due_date && (
                      <span style={{
                        fontSize: "11px",
                        fontFamily: "'JetBrains Mono', monospace",
                        color: overdue ? "var(--bad)" : soon ? "var(--warn)" : "var(--tx2)",
                        marginLeft: "auto",
                      }}>
                        {new Date(t.due_date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── LIST VIEW ──
function ListView({ tasks, projects, agents, members, onUpdate, onSelect }: {
  tasks: Task[];
  projects: Project[];
  agents: Agent[];
  members: Member[];
  onUpdate: (id: string, patch: Partial<Task>) => Promise<void>;
  onSelect: (t: Task) => void;
}) {
  if (tasks.length === 0) {
    return <EmptyState />;
  }

  return (
    <div style={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "12px", overflow: "hidden" }}>
      {tasks.map(t => {
        const assignee = getAssignee(t, agents, members);
        const overdue = isOverdue(t.due_date, t.status);
        const soon = isSoonDue(t.due_date, t.status);

        return (
          <div
            key={t.id}
            style={{
              display: "grid",
              gridTemplateColumns: "18px 1fr 90px 100px 80px",
              gap: "12px",
              alignItems: "center",
              padding: "8px 12px",
              borderBottom: "1px solid var(--line2)",
              cursor: "pointer",
              transition: "background .12s",
            }}
            onClick={() => onSelect(t)}
            onMouseEnter={e => (e.currentTarget.style.background = "var(--panel2)")}
            onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
          >
            <span
              onClick={e => { e.stopPropagation(); onUpdate(t.id, { status: t.status === "done" ? "todo" : "done" }); }}
              style={{
                width: "15px",
                height: "15px",
                border: `1.5px solid ${t.status === "done" ? "var(--ok)" : "var(--tx3)"}`,
                borderRadius: "5px",
                flexShrink: 0,
                background: t.status === "done" ? "var(--ok)" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "10px",
                color: "#fff",
              }}
            >{t.status === "done" ? "✓" : ""}</span>

            <span style={{ fontSize: "13px", color: "var(--tx)" }}>{t.title}</span>

            {t.priority ? <PriorityPill priority={t.priority} /> : <span />}

            {assignee ? (
              <span style={{
                fontSize: "11px",
                padding: "3px 8px",
                borderRadius: "6px",
                background: assignee.bg,
                color: assignee.color,
                textAlign: "center",
              }}>
                {assignee.label}
              </span>
            ) : (
              <span style={{ fontSize: "11px", color: "var(--tx3)", textAlign: "center" }}>—</span>
            )}

            {t.due_date ? (
              <span style={{
                fontSize: "11px",
                fontFamily: "'JetBrains Mono', monospace",
                color: overdue ? "var(--bad)" : soon ? "var(--warn)" : "var(--tx2)",
                textAlign: "right",
              }}>
                {new Date(t.due_date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── HELPERS ──
function PriorityPill({ priority }: { priority: string }) {
  const cfg = PRIORITY_CFG[priority] || PRIORITY_CFG.med;
  return (
    <span style={{
      fontSize: "10px",
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: "6px",
      textTransform: "uppercase",
      letterSpacing: ".3px",
      background: cfg.bg,
      color: cfg.color,
      flexShrink: 0,
    }}>
      {cfg.label}
    </span>
  );
}

function getAssignee(task: Task, agents: Agent[], members: Member[]) {
  if (!task.assignee_id) return null;
  if (task.assignee_type === "agent") {
    const a = agents.find(ag => ag.id === task.assignee_id);
    if (!a) return null;
    return {
      label: `${a.emoji} ${a.name}`,
      bg: "var(--acc-dim)",
      color: "var(--acc)",
    };
  } else {
    const m = members.find(mb => String(mb.id) === task.assignee_id);
    if (!m) return null;
    return {
      label: `👤 ${m.name}`,
      bg: "rgba(246,178,74,.14)",
      color: "var(--warn)",
    };
  }
}

function EmptyState() {
  return (
    <div style={{
      background: "var(--panel)",
      border: "1px solid var(--line)",
      borderRadius: "12px",
      padding: "60px 20px",
      textAlign: "center",
      color: "var(--tx3)",
    }}>
      Sin tareas todavía
    </div>
  );
}

// ── TASK DETAIL MODAL ──
function TaskDetailModal({ task, projects, agents, members, onClose, onUpdate, onDelete }: {
  task: Task;
  projects: Project[];
  agents: Agent[];
  members: Member[];
  onClose: () => void;
  onUpdate: (patch: Partial<Task>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority || "med");
  const [dueDate, setDueDate] = useState(task.due_date || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdate({ title, description, status, priority, due_date: dueDate || undefined });
    setSaving(false);
  };

  const proj = projects.find(p => p.id === task.project_id);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", backdropFilter: "blur(4px)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "16px",
          width: "100%", maxWidth: "560px", padding: "24px", display: "flex", flexDirection: "column", gap: "16px",
          boxShadow: "0 24px 80px rgba(0,0,0,.4)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            {proj && (
              <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "var(--tx3)", marginBottom: "6px" }}>
                <span style={{ width: "7px", height: "7px", borderRadius: "2px", background: proj.color || "var(--tx3)" }} />
                {proj.emoji} {proj.name}
              </div>
            )}
            <div style={{ fontSize: "11px", color: "var(--tx3)" }}>Creada {new Date(task.created_at).toLocaleDateString("es-ES")}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--tx3)", padding: "2px" }}>
            <X size={18} />
          </button>
        </div>

        {/* Título */}
        <textarea
          value={title}
          onChange={e => setTitle(e.target.value)}
          rows={2}
          style={{
            background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: "8px",
            padding: "10px 12px", fontSize: "15px", fontWeight: 600, color: "var(--tx)",
            resize: "none", fontFamily: "inherit", lineHeight: 1.35, width: "100%", boxSizing: "border-box",
          }}
        />

        {/* Descripción */}
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Descripción (opcional)…"
          rows={3}
          style={{
            background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: "8px",
            padding: "10px 12px", fontSize: "13px", color: "var(--tx2)", resize: "vertical",
            fontFamily: "inherit", lineHeight: 1.5, width: "100%", boxSizing: "border-box",
          }}
        />

        {/* Campos */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Estado</span>
            <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
              <option value="todo">Pendiente</option>
              <option value="doing">En progreso</option>
              <option value="review">En revisión</option>
              <option value="done">Hecho</option>
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Prioridad</span>
            <select value={priority} onChange={e => setPriority(e.target.value)} style={selectStyle}>
              <option value="low">Baja</option>
              <option value="med">Media</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Vence</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={selectStyle} />
          </label>
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
          <button
            onClick={async () => { if (confirm("¿Eliminar esta tarea?")) await onDelete(); }}
            style={{ background: "none", border: "none", color: "var(--bad)", fontSize: "12px", cursor: "pointer", padding: "6px 0" }}
          >
            Eliminar tarea
          </button>
          <div style={{ display: "flex", gap: "8px" }}>
            {status !== "done" && (
              <button
                onClick={async () => { setSaving(true); await onUpdate({ title, description, priority, due_date: dueDate||undefined, status: "done" }); setSaving(false); }}
                style={{ padding: "8px 14px", background: "var(--ok)", color: "#fff", border: "none", borderRadius: "8px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer" }}
              >
                ✓ Marcar hecha
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: "8px 16px", background: "var(--acc)", color: "#06101f", border: "none", borderRadius: "8px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer", opacity: saving ? .6 : 1 }}
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TASK CREATE MODAL ──
function TaskCreateModal({ projects, agents, members, defaultProjectId, onClose, onCreate }: {
  projects: Project[];
  agents: Agent[];
  members: Member[];
  defaultProjectId: string | null;
  onClose: () => void;
  onCreate: (data: Partial<Task>) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("todo");
  const [priority, setPriority] = useState("med");
  const [dueDate, setDueDate] = useState("");
  const [projectId, setProjectId] = useState(defaultProjectId || "");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!title.trim()) return;
    setSaving(true);
    await onCreate({ title, description, status, priority, due_date: dueDate || undefined, project_id: projectId || undefined });
    setSaving(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", backdropFilter: "blur(4px)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "16px",
          width: "100%", maxWidth: "500px", padding: "24px", display: "flex", flexDirection: "column", gap: "14px",
          boxShadow: "0 24px 80px rgba(0,0,0,.4)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 700, color: "var(--tx)" }}>Nueva tarea</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--tx3)" }}>
            <X size={18} />
          </button>
        </div>

        {/* Título */}
        <input
          autoFocus
          placeholder="Título de la tarea…"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) handleCreate(); }}
          style={{
            background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: "8px",
            padding: "10px 12px", fontSize: "14px", color: "var(--tx)", fontFamily: "inherit", width: "100%", boxSizing: "border-box",
          }}
        />

        {/* Descripción */}
        <textarea
          placeholder="Descripción (opcional)…"
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
          style={{
            background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: "8px",
            padding: "10px 12px", fontSize: "13px", color: "var(--tx2)", resize: "vertical",
            fontFamily: "inherit", lineHeight: 1.5, width: "100%", boxSizing: "border-box",
          }}
        />

        {/* Campos */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Proyecto</span>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} style={selectStyle}>
              <option value="">Sin proyecto</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.emoji} {p.name}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Prioridad</span>
            <select value={priority} onChange={e => setPriority(e.target.value)} style={selectStyle}>
              <option value="low">Baja</option>
              <option value="med">Media</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Estado</span>
            <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
              <option value="todo">Pendiente</option>
              <option value="doing">En progreso</option>
              <option value="review">En revisión</option>
              <option value="done">Hecho</option>
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "var(--tx3)", textTransform: "uppercase", letterSpacing: ".5px" }}>Fecha límite</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={selectStyle} />
          </label>
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
          <button onClick={onClose} style={{ padding: "8px 14px", background: "var(--panel2)", color: "var(--tx2)", border: "1px solid var(--line)", borderRadius: "8px", fontSize: "12.5px", cursor: "pointer" }}>
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !title.trim()}
            style={{ padding: "8px 18px", background: "var(--acc)", color: "#06101f", border: "none", borderRadius: "8px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer", opacity: (saving || !title.trim()) ? .5 : 1 }}
          >
            {saving ? "Creando…" : "Crear tarea"}
          </button>
        </div>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  background: "var(--panel2)",
  border: "1px solid var(--line)",
  borderRadius: "7px",
  padding: "6px 10px",
  fontSize: "12px",
  color: "var(--tx2)",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};
